import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import api from "../../api/client";

const emptyFaq = () => ({ question: "", answer: "" });
const emptySocial = () => ({ platform: "", url: "" });

export default function ChatbotSettings() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const [form, setForm] = useState({
    companyName: "",
    email: "",
    contactNumber: "",
    whatsappNumber: "",
    location: "",
    website: "",
    description: "",
    workingHours: "",
    pricingNotes: "",
    welcomeMessage: "",
    servicesText: "",
    faqs: [emptyFaq()],
    socialLinks: [emptySocial()],
  });

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/api/chatbot/settings");
      setForm({
        companyName: data.companyName || "",
        email: data.email || "",
        contactNumber: data.contactNumber || "",
        whatsappNumber: data.whatsappNumber || "",
        location: data.location || "",
        website: data.website || "",
        description: data.description || "",
        workingHours: data.workingHours || "",
        pricingNotes: data.pricingNotes || "",
        welcomeMessage: data.welcomeMessage || "",
        servicesText: (data.services || []).join("\n"),
        faqs: data.faqs?.length ? data.faqs : [emptyFaq()],
        socialLinks: data.socialLinks?.length ? data.socialLinks : [emptySocial()],
      });
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const updateFaq = (index, key, value) => {
    setForm((f) => {
      const faqs = [...f.faqs];
      faqs[index] = { ...faqs[index], [key]: value };
      return { ...f, faqs };
    });
  };

  const updateSocial = (index, key, value) => {
    setForm((f) => {
      const socialLinks = [...f.socialLinks];
      socialLinks[index] = { ...socialLinks[index], [key]: value };
      return { ...f, socialLinks };
    });
  };

  const onSave = async () => {
    setErr(null);
    setMsg(null);
    setSaving(true);
    try {
      const services = form.servicesText
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const faqs = form.faqs.filter((f) => f.question?.trim() || f.answer?.trim());
      const socialLinks = form.socialLinks.filter((s) => s.platform?.trim() || s.url?.trim());

      await api.post("/api/chatbot/settings", {
        companyName: form.companyName.trim(),
        email: form.email.trim(),
        contactNumber: form.contactNumber.trim(),
        whatsappNumber: form.whatsappNumber.trim(),
        location: form.location.trim(),
        website: form.website.trim(),
        description: form.description.trim(),
        workingHours: form.workingHours.trim(),
        pricingNotes: form.pricingNotes.trim(),
        welcomeMessage: form.welcomeMessage.trim(),
        services,
        faqs,
        socialLinks,
      });
      setMsg("Chatbot settings saved. The website assistant will use these details immediately.");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title="CHATBOT" subtitle="Manage enquiry assistant business details" />

      {err ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
          {err}
        </Alert>
      ) : null}
      {msg ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg}
        </Alert>
      ) : null}

      <Paper sx={{ p: 3, backgroundColor: colors.primary[400] }}>
        <Typography variant="h5" sx={{ mb: 2, color: colors.grey[100] }}>
          Business information
        </Typography>
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
          <TextField
            label="Company Name"
            fullWidth
            value={form.companyName}
            onChange={(e) => updateField("companyName", e.target.value)}
          />
          <TextField
            label="Email"
            fullWidth
            value={form.email}
            onChange={(e) => updateField("email", e.target.value)}
          />
          <TextField
            label="Contact Number"
            fullWidth
            value={form.contactNumber}
            onChange={(e) => updateField("contactNumber", e.target.value)}
          />
          <TextField
            label="WhatsApp Number"
            fullWidth
            value={form.whatsappNumber}
            onChange={(e) => updateField("whatsappNumber", e.target.value)}
          />
          <TextField
            label="Location"
            fullWidth
            value={form.location}
            onChange={(e) => updateField("location", e.target.value)}
          />
          <TextField
            label="Website"
            fullWidth
            value={form.website}
            onChange={(e) => updateField("website", e.target.value)}
          />
        </Box>

        <TextField
          label="Welcome Message"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
          value={form.welcomeMessage}
          onChange={(e) => updateField("welcomeMessage", e.target.value)}
        />

        <TextField
          label="Company Description"
          fullWidth
          multiline
          minRows={3}
          sx={{ mt: 2 }}
          value={form.description}
          onChange={(e) => updateField("description", e.target.value)}
        />

        <TextField
          label="Working Hours"
          fullWidth
          sx={{ mt: 2 }}
          value={form.workingHours}
          onChange={(e) => updateField("workingHours", e.target.value)}
        />

        <TextField
          label="Pricing Notes"
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
          value={form.pricingNotes}
          onChange={(e) => updateField("pricingNotes", e.target.value)}
        />

        <TextField
          label="Services (one per line)"
          fullWidth
          multiline
          minRows={5}
          sx={{ mt: 2 }}
          value={form.servicesText}
          onChange={(e) => updateField("servicesText", e.target.value)}
          helperText="Each line becomes a service the chatbot can mention."
        />

        <Divider sx={{ my: 3, borderColor: colors.grey[700] }} />

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" color={colors.grey[100]}>
            Social links
          </Typography>
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={() => setForm((f) => ({ ...f, socialLinks: [...f.socialLinks, emptySocial()] }))}
          >
            Add link
          </Button>
        </Box>
        {form.socialLinks.map((link, i) => (
          <Box key={i} display="flex" gap={1} mb={1} alignItems="flex-start">
            <TextField
              label="Platform"
              size="small"
              sx={{ flex: 1 }}
              value={link.platform}
              onChange={(e) => updateSocial(i, "platform", e.target.value)}
            />
            <TextField
              label="URL"
              size="small"
              sx={{ flex: 2 }}
              value={link.url}
              onChange={(e) => updateSocial(i, "url", e.target.value)}
            />
            <IconButton
              color="error"
              aria-label="Remove social link"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  socialLinks: f.socialLinks.filter((_, idx) => idx !== i),
                }))
              }
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        ))}

        <Divider sx={{ my: 3, borderColor: colors.grey[700] }} />

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" color={colors.grey[100]}>
            FAQs
          </Typography>
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={() => setForm((f) => ({ ...f, faqs: [...f.faqs, emptyFaq()] }))}
          >
            Add FAQ
          </Button>
        </Box>
        {form.faqs.map((faq, i) => (
          <Paper key={i} sx={{ p: 2, mb: 2, backgroundColor: colors.primary[500] }}>
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <IconButton
                size="small"
                color="error"
                aria-label="Remove FAQ"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    faqs: f.faqs.filter((_, idx) => idx !== i),
                  }))
                }
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              label="Question"
              fullWidth
              size="small"
              sx={{ mb: 1 }}
              value={faq.question}
              onChange={(e) => updateFaq(i, "question", e.target.value)}
            />
            <TextField
              label="Answer"
              fullWidth
              multiline
              minRows={2}
              size="small"
              value={faq.answer}
              onChange={(e) => updateFaq(i, "answer", e.target.value)}
            />
          </Paper>
        ))}

        <Button variant="contained" color="secondary" disabled={saving} onClick={onSave} sx={{ mt: 2 }}>
          {saving ? "Saving…" : "Save chatbot settings"}
        </Button>
      </Paper>
    </Box>
  );
}
